// A king arriving under power: the flight profile, as arithmetic.
//
// Pure and unit-free — height as a fraction of however high it starts, time as
// a fraction of however long it takes — so the numbers that decide how it
// *looks* live at the call site and the shape of the thing is testable without
// a scene.
//
// The profile is a real one rather than an eased curve, because the whole
// point of the shot is that something is being *flown*: it drops unpowered,
// the thrusters light, and the burn is exactly hard enough to arrive at zero.
// An ease-out would land just as softly and say nothing about why.

/**
 * The fraction of the flight spent falling before the burn starts.
 *
 * Slightly over half. Below about a third the burn is so gentle it reads as an
 * ease and the flame looks decorative; above about two thirds the thing is
 * still travelling fast very close to the board and reads as a crash it got
 * away with.
 */
const BRAKE_AT = 0.55;

/**
 * How much of the throttle is already open at the moment the burn starts.
 *
 * The burn is a *constant* deceleration, so physically the flame should snap
 * to full and hold — which looks like a switch being thrown. Opening it over a
 * short ramp is the one liberty taken with the profile, and it is the
 * difference between an engine lighting and a light turning on.
 */
const LIGHT_SECONDS = 0.09;

/** How long the flame takes to die once the weight is on the board. */
const CUT_SECONDS = 0.14;

export interface Descent {
  /** Height above the destination, as a fraction of the starting height. */
  height: number;
  /** How hard the engines are burning, 0..1. Drives the flame. */
  throttle: number;
  /** True once it is down and the flame has finished dying. */
  done: boolean;
}

/**
 * Where it is, and how hard it is burning, `t` of the way through.
 *
 * Derived rather than tabulated. Falling for `BRAKE_AT` of the flight under a
 * constant `g` and then braking to rest over the remainder fixes everything
 * else: total drop works out as `g·b·T/2`, so `g` follows from the height, and
 * the burn's deceleration follows from the speed it has to cancel.
 *
 * `t` past 1 is the flame dying with the piece already down, which is why this
 * takes the elapsed time in seconds as well — the cut-out is a wall-clock
 * length and not a share of a flight whose duration is a tuning knob.
 */
export function descentAt(t: number, elapsed: number, seconds: number): Descent {
  if (t >= 1) {
    const after = elapsed - seconds;
    return {
      height: 0,
      throttle: Math.max(0, 1 - after / CUT_SECONDS),
      done: after >= CUT_SECONDS
    };
  }

  const b = BRAKE_AT;
  // Fall `b` of the way in `b` of the time: `H = g·b·T/2` with T normalised to
  // 1, so `g = 2/b` in these units.
  const g = 2 / b;

  if (t <= b) {
    // Unpowered. Nothing is burning, and it is speeding up.
    return { height: Math.max(0, 1 - 0.5 * g * t * t), throttle: 0, done: false };
  }

  // The burn. Constant deceleration, sized to cancel exactly the speed the
  // fall built up over exactly the time that is left.
  const v = g * b;
  const decel = v / (1 - b);
  const s = t - b;
  const fallen = b + v * s - 0.5 * decel * s * s;
  const lighting = Math.min(1, (s * seconds) / LIGHT_SECONDS);
  return { height: Math.max(0, 1 - fallen), throttle: lighting, done: false };
}
